import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from 'primereact/button';
import { Column } from 'primereact/column';
import { DataTable } from 'primereact/datatable';
import { Dialog } from 'primereact/dialog';
import { Dropdown } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import { Message } from 'primereact/message';
import { flagAuditsApi, flagDefinitionsApi, flagLookupsApi } from '../../api/flagSystemApi';
import {
  AUDIT_EMPTY, auditActionLabel, auditActorLabel, auditChanges, auditRoleLabel,
  defaultAuditFilters, formatAuditInstant, validateAuditFilters,
} from '../../utils/flagAudit';
import { Field, FlagPage, ResourceState, useRemoteResource } from './FlagSystemCommon';

const contextFields = audit => [
  ['Audit record ID', audit.id], ['Customer ID', audit.customerId],
  ['Performed by', auditActorLabel(audit)], ['Recorded acting account ID', audit.userId],
  ['Role', auditRoleLabel(audit)], ['Recorded role', audit.role],
  ['Action', auditActionLabel(audit)], ['Recorded action identifier', audit.action],
  ['Date/time (British time)', formatAuditInstant(audit.occurredAt)],
  ['Flag', audit.flagName], ['Recorded flag ID', audit.flagId],
  ['Member ID', audit.memberId], ['Location', audit.locationName], ['Recorded location ID', audit.locationId],
];
const AuditDetails = ({ customerId, auditId, onAccessError }) => {
  const [missing, setMissing] = useState(false);
  const load = useCallback(() => flagAuditsApi.get(customerId, auditId), [customerId, auditId]);
  const handleError = useCallback(error => {
    setMissing(Boolean(error.response && error.response.status === 404));
    onAccessError(error);
  }, [onAccessError]);
  const resource = useRemoteResource(load, handleError);
  if (missing) return <Message severity="warn" text="This audit record is unavailable in your permitted customer scope. Close this dialog and refresh the audit list." />;
  const data = resource.data;
  const changes = data ? auditChanges(data.oldValues, data.newValues) : null;
  return <ResourceState resource={resource}>
    {data && <>
      <dl className="flag-audit-context">
        {contextFields(data.audit).map(([name, value]) => <div key={name}><dt>{name}</dt><dd>{value === null || value === undefined || value === '' ? AUDIT_EMPTY : value}</dd></div>)}
      </dl>
      <p className="flag-note">Names are current display labels, not guaranteed historical snapshots. Recorded IDs and role are shown above.</p>
      <h5>Comment</h5><p className="flag-audit-text">{data.audit.comment || AUDIT_EMPTY}</p>
      {data.changeDetailsWarning && <Message severity="warn" text={data.changeDetailsWarning} />}
      {changes.warnings.map(text => <div key={text} className="my-2"><Message severity="warn" text={text} /></div>)}
      <h5 className="mt-4">{changes.mode === 'created' ? 'Recorded new configuration' :
        changes.mode === 'removed' ? 'Recorded previous configuration' : 'Recorded changes'}</h5>
      {!data.changeDetailsAvailable || changes.mode === 'none' ? <p>Detailed changes were not recorded or are unavailable for this event.</p> :
        !changes.rows.length ? <p>No changed configuration fields were recorded.</p> :
          <DataTable value={changes.rows} dataKey="key" responsiveLayout="scroll">
            <Column field="label" header="Field" />
            {changes.mode !== 'created' && <Column field="before" header={changes.mode === 'removed' ? 'Recorded value' : 'Before'}
              body={row => <span className="flag-audit-text">{row.before}</span>} />}
            {changes.mode !== 'removed' && <Column field="after" header={changes.mode === 'created' ? 'Recorded value' : 'After'}
              body={row => <span className="flag-audit-text">{row.after}</span>} />}
          </DataTable>}
    </>}
  </ResourceState>;
};

const FlagAuditLog = ({ account, onAccessError }) => {
  const { customerId } = account;
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [draft, setDraft] = useState(() => ({ ...defaultAuditFilters(), memberId: searchParams.get('memberId') || '' }));
  const [query, setQuery] = useState(() => ({ ...draft, page: 0, pageSize: 50 }));
  const [errors, setErrors] = useState({});
  const [actionOptions, setActionOptions] = useState({});
  const [selected, setSelected] = useState(null);
  const load = useCallback(() => flagAuditsApi.list(customerId, query), [customerId, query]);
  const resource = useRemoteResource(load, onAccessError);
  const loadLookups = useCallback(async () => {
    const [flags, locations] = await Promise.all([flagDefinitionsApi.list(customerId), flagLookupsApi.locations(customerId)]);
    return { flags, locations };
  }, [customerId]);
  const lookups = useRemoteResource(loadLookups, onAccessError);
  useEffect(() => {
    if (resource.data) setActionOptions(resource.data.actionOptions);
  }, [resource.data]);
  const change = (key, value) => setDraft(previous => ({ ...previous, [key]: value }));
  const apply = event => {
    event.preventDefault();
    const validation = validateAuditFilters(draft);
    setErrors(validation);
    if (!Object.keys(validation).length) {
      setSelected(null); setQuery(previous => ({ ...draft, page: 0, pageSize: previous.pageSize }));
    }
  };
  const reset = () => {
    const filters = defaultAuditFilters();
    setDraft(filters); setErrors({}); setSelected(null);
    setQuery(previous => ({ ...filters, page: 0, pageSize: previous.pageSize }));
  };
  const page = resource.data;
  const filterOptions = lookups.data;
  return <FlagPage title="Flag Audit Log" actions={<>
    <Button label="Back To Flag Management" icon="pi pi-arrow-left" className="p-button-text" onClick={() => navigate('/support/flags')} />
    <Button label="Refresh Audit Log" icon="pi pi-refresh" className="p-button-text" disabled={resource.loading} onClick={() => { setSelected(null); resource.reload(); }} />
  </>}>
    <p className="flag-note">Read-only activity for your customer. Date filters use inclusive British calendar dates; times are displayed in Europe/London.</p>
    <form onSubmit={apply}>
      <div className="flag-audit-filters">
        <Field id="audit-from" label="From date (British)" error={errors.fromDate}>
          <InputText id="audit-from" type="date" value={draft.fromDate} onChange={event => change('fromDate', event.target.value)} />
        </Field>
        <Field id="audit-to" label="To date (British)" error={errors.toDate}>
          <InputText id="audit-to" type="date" value={draft.toDate} onChange={event => change('toDate', event.target.value)} />
        </Field>
        <Field id="audit-action" label="Action">
          <Dropdown inputId="audit-action" options={Object.entries(actionOptions).map(([value, text]) => ({ value, label: text }))}
            value={draft.action || null} onChange={event => change('action', event.value || '')} showClear placeholder="All actions" />
        </Field>
        <Field id="audit-member" label="Member ID (exact)">
          <InputText id="audit-member" value={draft.memberId} onChange={event => change('memberId', event.target.value)} placeholder="Including leading zeros" />
        </Field>
        <Field id="audit-flag" label="Flag" error={errors.flagId}>
          <Dropdown inputId="audit-flag" value={draft.flagId}
            options={filterOptions ? filterOptions.flags.map(flag => ({ value: flag.flagId, label: `${flag.name}${flag.active === false ? ' (disabled)' : ''}` })) : []}
            onChange={event => change('flagId', event.value)} showClear placeholder="All flags" disabled={!filterOptions} />
        </Field>
        <Field id="audit-location" label="Location" error={errors.locationId}>
          <Dropdown inputId="audit-location" value={draft.locationId}
            options={filterOptions ? filterOptions.locations.map(location => ({ value: location.id, label: location.name })) : []}
            onChange={event => change('locationId', event.value)} showClear placeholder="All locations" disabled={!filterOptions} />
        </Field>
      </div>
      <div className="flex gap-2 flex-wrap my-3">
        <Button type="submit" label="Apply Filters" icon="pi pi-filter" disabled={resource.loading} />
        <Button type="button" label="Reset Filters" className="p-button-text" onClick={reset} disabled={resource.loading} />
      </div>
    </form>
    {lookups.error && <div className="flex gap-2 flex-wrap my-3">
      <Message severity="warn" text={`Flag/location filter options could not be loaded. ${lookups.error}`} />
      <Button label="Retry Filter Options" className="p-button-text" onClick={lookups.reload} disabled={lookups.loading} />
    </div>}
    <ResourceState resource={resource}>
      {page && <>
        <p className="flag-note">{page.totalCount} matching {page.totalCount === 1 ? 'event' : 'events'}.
          {page.totalPages > 0 && ` Page ${page.page + 1} of ${page.totalPages}.`}</p>
        <DataTable value={page.items} dataKey="id" lazy paginator={page.totalCount > 0} first={query.page * query.pageSize}
          rows={query.pageSize} totalRecords={page.totalCount} rowsPerPageOptions={[25, 50, 100, 200]}
          onPage={event => { setSelected(null); setQuery(previous => ({ ...previous, page: event.rows !== previous.pageSize ? 0 : event.page, pageSize: event.rows })); }}
          emptyMessage="No matching audit activity." responsiveLayout="scroll">
          <Column header="Date/time (British)" body={audit => formatAuditInstant(audit.occurredAt)} />
          <Column header="Action" body={auditActionLabel} />
          <Column header="Flag" body={audit => audit.flagName || AUDIT_EMPTY} />
          <Column header="Member ID" body={audit => audit.memberId === null || audit.memberId === undefined || audit.memberId === '' ? AUDIT_EMPTY : audit.memberId} />
          <Column header="Location" body={audit => audit.locationName || AUDIT_EMPTY} />
          <Column header="Performed by" body={auditActorLabel} />
          <Column header="Role" body={auditRoleLabel} />
          <Column header="Comment" body={audit => <span className="flag-audit-comment">{audit.comment || AUDIT_EMPTY}</span>} />
          <Column header="Details" body={audit => <Button label="View Details" icon="pi pi-search" className="p-button-text p-button-sm"
            onClick={() => setSelected(audit.id)} />} />
        </DataTable>
      </>}
    </ResourceState>
    <Dialog header="Audit Details" visible={selected !== null} onHide={() => setSelected(null)}
      className="flag-editor flag-audit-details" modal draggable={false} resizable={false}
      footer={<Button label="Close Details" className="p-button-text" onClick={() => setSelected(null)} />}>
      {selected !== null && <AuditDetails key={selected} auditId={selected} customerId={customerId} onAccessError={onAccessError} />}
    </Dialog>
  </FlagPage>;
};
export default FlagAuditLog;
